import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import Router from 'next/router';
import { Button } from '@/components/shadcn/button';
import { processFile } from '@/utils/files';
import { FilesWithLocations, ScoutLocation } from '@/types/location';
import { buildErrorMessage } from '@/utils/scout';
import { registerLocations } from '@/api/location';
import { colors } from '@/utils/styles';
import UploadIcon from '@/components/ui/icons/UploadIcon';
import LoadingIcon from '@/components/ui/icons/LoadingIcon';
import * as Toast from '@/components/ui/toast/Toast';
import { containsFailures } from '@/utils/validation';
import { useToaster } from '@/hooks/useToaster';
import Divider from '@/components/ui/shared/Divider';
import { Views } from '@/types/view';
import { openLinkInNewTab } from '@/utils/url';
import { useUser } from '@/hooks/useUser';

type Error = { message: string };

interface Props {
  callback: (
    locations: ScoutLocation[],
    mode: 'add' | 'delete',
    hasFailures: boolean,
  ) => void;
  filesWithLocations: FilesWithLocations;
  setFilesWithLocations: Dispatch<SetStateAction<FilesWithLocations>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setActiveView: Dispatch<SetStateAction<Views>>;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

export default function Dropzone({
  callback,
  setFilesWithLocations,
  isLoading,
  setIsLoading,
  setActiveView,
  setShowModal,
}: Props) {
  const { setExpand } = useToaster();
  const { user } = useUser();

  const addFilesAndLocations = useCallback(
    async (newLocations: ScoutLocation[], file: File) => {
      let locations = newLocations;
      let hasFailures = false;

      if (!user) return;

      const response = await registerLocations(
        locations,
        user.currentOrganization,
      );

      if ('error' in response || !response.locations) {
        toast.custom(t => (
          <Toast.Default
            title="Try Again"
            description="We were unable to add the location, please try again."
            buttons={[
              {
                label: 'Dismiss',
                onClick: () => toast.dismiss(t),
                variant: 'secondary',
                size: 'fw',
              },
            ]}
          />
        ));

        return;
      }

      if (containsFailures(response.failures)) {
        hasFailures = true;
        const { failures } = response;
        const { errorMessage, reasons } = buildErrorMessage(failures);
        setExpand(true);
        toast.custom(
          t => (
            <Toast.Default
              title="Some Locations Failed"
              description={
                <>
                  {errorMessage}
                  <br />
                  {reasons}
                </>
              }
              buttons={[
                {
                  label: 'Dismiss',
                  onClick: () => toast.dismiss(t),
                  variant: 'secondary',
                  size: 'fw',
                },
              ]}
            />
          ),
          { onAutoClose: () => setTimeout(() => setExpand(false), 500) },
        );
      }

      if (response.locations) {
        locations = response.locations;
      }

      if (locations.length > 0) {
        toast.custom(t => (
          <Toast.Default
            title="Locations Added"
            description="New locations successfully added to Scout."
            buttons={[
              {
                label: 'Dismiss',
                onClick: () => toast.dismiss(t),
                variant: 'secondary',
                size: 'fw',
              },
            ]}
          />
        ));
      }

      setFilesWithLocations(prevState => {
        const copy = { ...prevState };
        copy[file.name] = { file, locations };
        callback(locations, 'add', hasFailures);
        return copy;
      });

      setIsLoading(false);
    },
    [callback, setFilesWithLocations, setIsLoading, setExpand, user],
  );

  const handleError = useCallback(
    (error: Error | string) => {
      setIsLoading(false);
      toast.custom(t => (
        <Toast.Default
          title="Try Again"
          description={typeof error === 'string' ? error : error.message}
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
            {
              label: 'Documentation',
              onClick: () =>
                openLinkInNewTab(
                  'https://docs.hivemapper.com/use-the-map/scout/scout-hosted-service',
                ),
              size: 'fw',
            },
          ]}
        />
      ));
    },
    [setIsLoading],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        handleError('Unsupported file type');
      }

      for (const file of acceptedFiles) {
        const fileType = file.type;
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop()?.toLowerCase();

        setIsLoading(true);

        try {
          if (fileType === 'text/csv' || fileExtension === 'csv') {
            processFile(file, 'csv', addFilesAndLocations, handleError);
          } else if (
            fileType === 'application/geo+json' ||
            fileExtension === 'geojson'
          ) {
            processFile(file, 'geojson', addFilesAndLocations, handleError);
          } else {
            throw new Error('Unsupported file type');
          }
        } catch (error) {
          console.error('Error processing file.', error);
          handleError(error as { message: string } | string);
        }
      }
    },
    [addFilesAndLocations, handleError, setIsLoading],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/geo+json': ['.geojson'],
    },
  });

  const style: any = useMemo(() => {
    const baseStyle = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      minHeight: 106,
      flexDirection: 'column' as const,
      borderWidth: 1,
      borderRadius: 8,
      paddingTop: 24,
      paddingBottom: 24,
      paddingLeft: 8,
      paddingRight: 8,
      borderColor: colors.neutral[700],
      transition: 'border .24s ease-in-out',
      cursor: 'pointer',
    };

    const focusedStyle = {
      borderColor: colors.neutral[400],
    };

    const acceptStyle = {
      borderColor: colors.veridianSuccess[400],
    };

    const rejectStyle = {
      borderColor: colors.destructive,
    };

    return {
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    };
  }, [isFocused, isDragAccept, isDragReject]);

  return (
    <div className="flex flex-col items-center justify-center gap-2">
      <div className="font-bold">Upload Locations</div>
      <div {...getRootProps({ style })}>
        {isLoading ? (
          <LoadingIcon />
        ) : (
          <div className="flex flex-col items-center justify-center gap-2">
            <input {...getInputProps()} />
            <UploadIcon width={28} height={28} />
            {isDragActive ? (
              <p>Drop your files here!</p>
            ) : (
              <p>Drag and drop a CSV or GeoJSON file</p>
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between">
        <a
          className="text-xs"
          href="https://docs.hivemapper.com/use-the-map/scout/scout-hosted-service"
          target="_blank"
          rel="noreferrer">
          View Supported Formats
        </a>
      </div>
      <Divider />
      <Button
        size="fw"
        onClick={() => {
          setActiveView(Views.Map);
          setShowModal(false);
          Router.push(Views.Map, undefined, { shallow: true });
        }}>
        Add via Map
      </Button>
    </div>
  );
}
